// ReceiptPeek: a receipt shown in place, so Dawn can verify a match without leaving the board
// and losing her scroll position. Authed image blob (PDF in an iframe, image as img, a skeleton
// while it loads), the parsed header fields, status, and a link out to the full receipt. The
// frame is ui/Peek, non-modal: the board's own keys stay live while it is open.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetchAuthed } from '../apiHelpers';
import { buttonClass } from '../components/ui/Button';
import Peek from '../components/ui/Peek';
import Skeleton from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import { EmptyState } from '../components/ui/EmptyState';
import Tooltip from '../components/ui/Tooltip';
import { formatEventDate } from '../utils/date-utils';
import { Receipt, type ReceiptRequisitionLink } from './types';
import RequisitionChips from './RequisitionChip';
import StatusPill from './StatusPill';
import { formatCardLabel } from './cardLabel';

const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Math.abs(Number(v)).toFixed(2)}`);

// Name the saved file after the receipt, not the UUID, because the reason to save
// one is to hand it to somebody outside this app. Prefer what the buyer uploaded;
// fall back to vendor + date so an auditor's folder still sorts and reads.
const EXT: Record<string, string> = { 'application/pdf': 'pdf', 'image/png': 'png', 'image/jpeg': 'jpg', 'image/heic': 'heic', 'image/webp': 'webp' };
function downloadName(receipt: Receipt | null): string {
  if (receipt?.original_filename) return receipt.original_filename;
  const slug = (receipt?.vendor_name || 'receipt').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'receipt';
  const date = receipt?.receipt_date || '';
  return `${slug}${date ? `-${date}` : ''}.${EXT[receipt?.mime_type || ''] || 'jpg'}`;
}

const IconDownload: React.FC = () => (
  <svg aria-hidden="true" focusable="false" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
    <path d="M12 3a1 1 0 011 1v9.6l3.3-3.3a1 1 0 111.4 1.4l-5 5a1 1 0 01-1.4 0l-5-5a1 1 0 111.4-1.4l3.3 3.3V4a1 1 0 011-1z" fill="currentColor" />
    <path d="M4 15a1 1 0 011 1v3h14v-3a1 1 0 112 0v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a1 1 0 011-1z" fill="currentColor" />
  </svg>
);

export default function ReceiptPeek({ pid, onClose, requisitions }: {
  pid: string | null;
  onClose: () => void;
  /** The board's links for this receipt, when the board opened the peek. */
  requisitions?: ReceiptRequisitionLink[];
}) {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imgLoading, setImgLoading] = useState(false);

  // Parsed fields.
  useEffect(() => {
    if (!pid) { setReceipt(null); return; }
    let cancelled = false;
    setReceipt(null);
    apiFetchAuthed(`/receipts/${pid}`, { silent: true })
      .then((r: Receipt) => { if (!cancelled) setReceipt(r); })
      .catch(() => { /* header optional; the link out still works */ });
    return () => { cancelled = true; };
  }, [pid]);

  // Authed image/PDF blob (the proxy needs the Bearer header, same pattern as
  // ReceiptReviewPage). A plain <img src> can't send it.
  useEffect(() => {
    if (!pid) { setImageUrl(null); return; }
    let revoked = false;
    let url: string | null = null;
    setImageUrl(null);
    setImgLoading(true);
    (async () => {
      try {
        const resp = (await apiFetchAuthed(`/receipts/${pid}/image`, { skipJsonParse: true, silent: true })) as Response;
        if (!resp.ok || revoked) return;
        const blob = await resp.blob();
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch { /* image optional */ }
      finally { if (!revoked) setImgLoading(false); }
    })();
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [pid]);

  const isPdf = (receipt?.mime_type || '').includes('pdf');

  const footer = pid ? (
    <>
      <Link to={`/receipts/${pid}`} className={`${buttonClass('filled', 'md')} flex-1`}>Open full receipt</Link>
      {/* Saves the blob already fetched above, so no second authed request.
          Disabled until it lands rather than hidden, so the control does not
          appear late under the cursor.
          DROPPING `href` is what actually disables this, not aria-disabled or
          pointer-events: an anchor with no href is not a link, so it stays out
          of the tab order and off the Enter key. Keep href conditional. */}
      <Tooltip label="Save the receipt file">
        <a
          href={imageUrl || undefined}
          download={downloadName(receipt)}
          aria-disabled={!imageUrl}
          className={`${buttonClass('tonal', 'md')} flex-1 ${imageUrl ? '' : 'pointer-events-none !bg-surface-container !text-on-surface-disabled !shadow-none'}`}
        >
          <IconDownload />
          Download
        </a>
      </Tooltip>
    </>
  ) : null;

  return (
    <Peek
      open={!!pid}
      onClose={onClose}
      modal={false}
      label="Receipt preview"
      title={receipt?.vendor_name || 'Receipt'}
      meta={receipt ? <StatusPill status={receipt.status} /> : null}
      contentKey={pid}
      footer={footer}
    >
      <div className="space-y-4">
        <div className="flex min-h-[220px] items-center justify-center rounded-control bg-surface-container p-2">
          <FadeIn key={imgLoading ? 'loading' : imageUrl ? 'image' : 'none'} className="flex w-full items-center justify-center">
          {imgLoading ? (
            <Skeleton className="h-[240px] w-full" />
          ) : imageUrl ? (
            isPdf ? (
              <iframe src={imageUrl} title="Receipt PDF" className="h-[46vh] w-full rounded-inner bg-white" />
            ) : (
              <a href={imageUrl} target="_blank" rel="noreferrer" className="rounded-inner focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
                <img src={imageUrl} alt="Receipt" className="max-h-[46vh] max-w-full rounded-inner" />
              </a>
            )
          ) : (
            <EmptyState variant="line">No image</EmptyState>
          )}
          </FadeIn>
        </div>

        {receipt && (
          <FadeIn className="space-y-4">
            {/* The money reads like a ledger: right-aligned figures, the total last and heaviest. */}
            <div className="divide-y divide-outline-variant rounded-control bg-surface-container">
              <PeekMoney label="Subtotal" value={receipt.subtotal} />
              <PeekMoney label="Tax" value={receipt.tax} />
              <PeekMoney label="Tip" value={receipt.tip} />
              <PeekMoney label="Total" value={receipt.total} strong />
            </div>

            <dl className="m-0 space-y-2 text-sm">
              <PeekField label="Date">{receipt.receipt_date ? formatEventDate(receipt.receipt_date) : '—'}</PeekField>
              <PeekField label="Category">{receipt.category_label || '—'}</PeekField>
              <PeekField label="Card">
                {(receipt.card_label || receipt.card_last4)
                  ? formatCardLabel({ label: receipt.card_label || '', last4: receipt.card_last4 || null })
                  : '—'}
              </PeekField>
              <PeekField label="Owner">{receipt.owner_name || '—'}</PeekField>
              {receipt.event_name && <PeekField label="Event">{receipt.event_name}</PeekField>}
              {requisitions && requisitions.length > 0 && (
                <PeekField label="Request"><RequisitionChips requisitions={requisitions} className="justify-end" /></PeekField>
              )}
            </dl>

            {receipt.ai_remarks && (
              <div className="rounded-control bg-surface-container px-3 py-2 text-sm text-on-surface">
                <span className="mb-0.5 block text-xs font-semibold text-on-surface-variant">Reader's note</span>
                {receipt.ai_remarks}
              </div>
            )}
          </FadeIn>
        )}
      </div>
    </Peek>
  );
}

function PeekMoney({ label, value, strong }: { label: string; value: unknown; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className={`text-xs ${strong ? 'text-on-surface font-medium' : 'text-on-surface-variant'}`}>{label}</span>
      <span className={`font-mono tabular-nums ${strong ? 'text-on-surface text-sm font-semibold' : 'text-on-surface text-sm'}`}>{money(value)}</span>
    </div>
  );
}

function PeekField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-on-surface-variant shrink-0 pt-0.5">{label}</dt>
      <dd className="text-on-surface text-right min-w-0 truncate">{children}</dd>
    </div>
  );
}
